import { useEffect, useState } from 'react'
import { useParams, useNavigate, useSearchParams } from 'react-router-dom'
import {
  Box,
  Button,
  Flex,
  Heading,
  Table as ChakraTable,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  Spinner,
  Text,
  Badge,
  HStack,
  IconButton,
  useToast,
  Input,
  InputGroup,
} from '@chakra-ui/react'
import useSeasonChallenges from './hooks/useSeasonChallenges'
import Pagination from '@olagg/ui-kit/Pagination'
import { FaEye, FaPlus, FaTasks, FaTrash } from 'react-icons/fa'
import { AdminSeasonChallenge } from '@olagg/db-types'

const ChallengesIndex = () => {
  const { id: seasonId } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const toast = useToast()
  const { loading, getChallenges, deleteChallenge } = useSeasonChallenges()

  const [challenges, setChallenges] = useState<AdminSeasonChallenge[]>([])
  const [count, setCount] = useState(0);
  const [debouncedTitle, setDebouncedTitle] = useState('');
  const [filter, setFilter] = useState({
    title: searchParams.get('title') || '',
    page: Number(searchParams.get('page') || 1),
    limit: Number(searchParams.get('limit') || 10),
  })
  const { page, limit, title } = filter

  const onFilter = ({
    name,
    value
  }: { name: string, value: any }) => {
    const parsedValue = (name === 'page' || name === 'limit') ? Number(value) : value;
    setFilter({ ...filter, [name]: parsedValue })

    if (value) searchParams.set(name, value)
    else searchParams.delete(name)
    setSearchParams(searchParams)
  }

  const loadChallenges = async () => {
    if (!seasonId) return
    
    try {
      const response = await getChallenges({
        page,
        limit,
        title: debouncedTitle || undefined,
        game_season_id: seasonId
      })
      
      setChallenges(response.gameSeasonChallenges)
      setCount(response.meta.count)
    } catch (error) {
      toast({
        title: 'Error',
        description: 'No se pudieron cargar los desafíos',
        status: 'error',
        duration: 5000,
        isClosable: true,
      })
    }
  }

  // Debounce title filter
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedTitle(title)
    }, 500)

    return () => clearTimeout(timer)
  }, [title])

  useEffect(() => {
    if (seasonId) {
      loadChallenges()
    }
  }, [seasonId, page, limit, debouncedTitle])

  useEffect(() => {
    const urlPage = searchParams.get('page');
    const urlLimit = searchParams.get('limit');
    const urlTitle = searchParams.get('title');

    if (urlPage || urlLimit || urlTitle) {
      setFilter({
        title: urlTitle || '',
        page: Number(urlPage || 1),
        limit: Number(urlLimit || 10),
      });
    }
  }, []);

  const handleDelete = async (challengeId: number) => {
    if (window.confirm('¿Estás seguro de que quieres eliminar este desafío?')) {
      try {
        await deleteChallenge(challengeId.toString())
        toast({
          title: 'Desafío eliminado',
          description: 'El desafío ha sido eliminado exitosamente',
          status: 'success',
          duration: 3000,
          isClosable: true,
        })
        loadChallenges()
      } catch (error) {
        toast({
          title: 'Error',
          description: 'No se pudo eliminar el desafío',
          status: 'error',
          duration: 5000,
          isClosable: true,
        })
      }
    }
  }

  const formatDate = (date: string | Date) => {
    const dateObj = typeof date === 'string' ? new Date(date) : date
    return dateObj.toLocaleDateString('es-ES', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  if (loading && challenges.length === 0) {
    return (
      <Flex justify="center" align="center" h="200px">
        <Spinner size="lg" />
      </Flex>
    )
  }

  return (
    <Box color="white">
      {/* Header */}
      <Flex justify="space-between" align="center" mb={6} mx={2}>
        <Heading size="lg">Desafíos de la Temporada</Heading>
      </Flex>

      {/* Filters */}
      <Flex gap={4} mb={6} align="center">
        <InputGroup maxW="300px">
          <Input
            placeholder="Buscar por título..."
            value={title}
            onChange={(e) => onFilter({ name: 'title', value: e.target.value })}
            bg="gray.700"
            borderColor="gray.600"
            _placeholder={{ color: 'gray.400' }}
          />
        </InputGroup>

        <Button
          leftIcon={<FaPlus />}
          variant="outlinedGradient"
          onClick={() => navigate(`/seasons/${seasonId}/challenges/crear`)}
        >
          Crear Desafío
        </Button>
      </Flex>

      {/* Table */}
      <TableContainer bg="gray.800" borderRadius="md" mb={6}>
        <ChakraTable
          variant="striped"
          size="sm"
          colorScheme="olaggPurple"
          fontSize="18px"
          position='relative'
          whiteSpace={'normal'}
        >
          <Thead>
            <Tr>
              <Th color="gray.300">Día</Th>
              <Th color="gray.300">Título</Th>
              <Th color="gray.300">Descripción</Th>
              <Th color="gray.300">Tareas</Th>
              <Th color="gray.300">Monedas de la Garra</Th>
              <Th color="gray.300">Creado</Th>
              <Th color="gray.300">Acciones</Th>
            </Tr>
          </Thead>
          <Tbody>
            {challenges.length === 0 ? (
              <Tr>
                <Td colSpan={5} textAlign="center" py={8}>
                  <Text color="gray.400">
                    {title ? 'No se encontraron desafíos con ese criterio' : 'No hay desafíos creados'}
                  </Text>
                </Td>
              </Tr>
            ) : (
              challenges.map((challenge) => (
                <Tr key={challenge.id}>
                  <Td>
                    <Badge colorScheme="blue" fontSize="sm">
                      Día {challenge.dayNumber}
                    </Badge>
                  </Td>
                  <Td fontWeight="medium">{challenge.title}</Td>
                  <Td>
                    <Text noOfLines={2} maxW="300px">
                      {challenge.description || 'Sin descripción'}
                    </Text>
                  </Td>
                  <Td>
                    <Text noOfLines={2} maxW="300px">
                      {challenge.gameSeasonChallengeTasksCount || 0}
                    </Text>
                  </Td>
                  <Td>
                    <Text noOfLines={2} maxW="300px">
                      {challenge.clawCoinsReward || 0}
                    </Text>
                  </Td>
                  <Td color="gray.400" fontSize="sm">
                    {formatDate(challenge.createdAt)}
                  </Td>
                  <Td>
                    <HStack spacing={2}>
                      <IconButton
                        aria-label="Editar desafío"
                        icon={<FaEye />}
                        size="sm"
                        colorScheme="blue"
                        variant="ghost"
                        onClick={() => navigate(`/seasons/${seasonId}/challenges/${challenge.id}`)}
                      />
                      <IconButton
                        aria-label="Ver tareas"
                        icon={<FaTasks />}
                        size="sm"
                        colorScheme="green"
                        variant="ghost"
                        onClick={() => navigate(`/seasons/${seasonId}/challenges/${challenge.id}/tasks`)}
                        title="Ver tareas del desafío"
                      />
                      <IconButton
                        aria-label="Eliminar desafío"
                        icon={<FaTrash />}
                        size="sm"
                        colorScheme="red"
                        variant="ghost"
                        onClick={() => handleDelete(challenge.id)}
                      />
                    </HStack>
                  </Td>
                </Tr>
              ))
            )}
          </Tbody>
        </ChakraTable>
      </TableContainer>

      <Pagination
          params={{ limit, page, count }}
          onClick={page =>
            onFilter({
              name: 'page',
              value: String(page + 1)
            })
          }
          onLimitChange={val =>
            onFilter({
              name: 'limit',
              value: val
            })
          }
          hideLimitInput
        />
    </Box>
  )
}

export default ChallengesIndex
